import { t } from "i18next";
import { MicIcon, TimerIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { JSX } from "react";
import { useEffect, useMemo, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useSpeakerLabel } from "@/components/ui/visits/SpeakerTranscript";
import type { TranscriptionStreamText } from "@/hooks/transcriber/types";
import { liveTranscriptLines, withGaps } from "@/lib/live-transcript";
import { formatTimeMMSS } from "@/lib/utils";
import type { LiveTranscriptGap } from "@/store/transcriptionStreaming.store";
import { LiveTranscriptGapLine } from "./live-transcript-gap";

interface Props {
  transcriptions: TranscriptionStreamText[];
  loading: boolean;
  /** Words still being recognised, shown after the finished segments. */
  partialText?: string;
  /** Parts the live transcript missed, shown where they happened. */
  gaps?: LiveTranscriptGap[];
}

const NO_GAPS: LiveTranscriptGap[] = [];

export const TranscriptionList: React.FC<Props> = ({
  transcriptions,
  loading,
  partialText = "",
  gaps = NO_GAPS,
}): JSX.Element => {
  const endRef = useRef<HTMLDivElement>(null);
  // Diarized lines carry "Speaker 0:" (the stored format); show the
  // speaker as a label instead.
  const speakerLabel = useSpeakerLabel();
  const lines = useMemo(
    () => liveTranscriptLines(transcriptions, partialText),
    [transcriptions, partialText],
  );
  const finished = useMemo(
    () =>
      withGaps(
        lines.filter((line) => !line.partial),
        gaps,
      ),
    [lines, gaps],
  );
  const pending = lines.filter((line) => line.partial);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcriptions, loading, partialText, gaps]);

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-v3-panel bg-v3-panel p-4 h-80 overflow-y-auto font-v3 shadow-v3-hairline">
        {transcriptions.length === 0 &&
        !loading &&
        !partialText &&
        gaps.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-v3-text-3 text-[14px] gap-2">
            <div className="flex gap-2">
              <MicIcon className="w-6 h-6" />
              {t("components.transcriptionList.emptyState")}
            </div>
          </div>
        ) : (
          <ul className="space-y-3">
            <AnimatePresence initial={false}>
              {finished.map((item) =>
                item.kind === "gap" ? (
                  <LiveTranscriptGapLine
                    key={`gap-${item.gap.id}`}
                    gap={item.gap}
                  />
                ) : (
                  <motion.li
                    key={item.line.key}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-v3-surface-2 p-3 rounded-xl"
                    exit={{ opacity: 0, y: -10 }}
                    initial={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="flex items-center gap-1 text-[12.5px] tabular-nums text-v3-text-3">
                      <TimerIcon className="w-4 h-4" />
                      {formatTimeMMSS(item.line.startTime)}
                      {item.line.showSpeaker && item.line.speaker !== null && (
                        <span
                          className="ml-1 font-semibold text-v3-text-2"
                          data-testid="live-speaker"
                        >
                          {speakerLabel(
                            item.line.speaker,
                            item.line.assignment,
                          )}
                        </span>
                      )}
                    </div>
                    <div className="text-[14px] leading-relaxed text-v3-text">
                      {item.line.text}
                    </div>
                  </motion.li>
                ),
              )}
            </AnimatePresence>

            {(loading || partialText) && (
              <li className="bg-v3-surface-2 p-3 rounded-xl space-y-2">
                <div className="flex items-center gap-1 text-[12.5px] tabular-nums text-v3-text-3">
                  <TimerIcon className="w-4 h-4 text-v3-text-3" />
                  <Skeleton className="h-4 w-12" />
                </div>
                {pending.length ? (
                  <div
                    className="text-[14px] leading-relaxed text-v3-text-3"
                    data-testid="live-partial"
                  >
                    {pending.map((line) => (
                      <p key={line.key}>
                        {line.showSpeaker && line.speaker !== null && (
                          <span className="mr-1.5 text-[12.5px] font-semibold text-v3-text-2">
                            {speakerLabel(line.speaker, line.assignment)}
                          </span>
                        )}
                        {line.text}
                      </p>
                    ))}
                  </div>
                ) : (
                  <Skeleton className="h-5 w-3/4 rounded-md" />
                )}
              </li>
            )}

            <div ref={endRef} />
          </ul>
        )}
      </div>
    </div>
  );
};
